/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { LearnerDeadlineStatus } from '@lms-types/index';

/**
 * The one place the app formats a due date, a date-ish timestamp, or a
 * relative "how long ago" label (code-health plan item 1.5, completed by
 * duplicate-logic finding #17).
 *
 * The live walkthrough caught cards showing "Due Feb 5" for Feb 5 of *next
 * year* — against a current date in August, every future deadline read as
 * months overdue. The rule here: the year appears whenever the date is not
 * in the current year, and every card formats through this module instead of
 * a per-file copy (there were three, one of which also had the
 * timezone-shift bug the other two fixed).
 *
 * Finding #17 then found the UTC-midnight construction typed out eight times
 * in all. `formatDueDay` above covered the three list-view copies; the five
 * remaining copies formatted the *same* calendar day with a *different*
 * option set, so they are their own exports here rather than a silent change
 * to what a learner reads:
 *
 *   - `formatDueDateLong`    — always shows the year ("Sep 15, 2026")
 *   - `formatDueDateNumeric` — the browser's locale-default numeric form
 *
 * Do not collapse these into `formatDueDay`: it omits the year in the current
 * year, and every one of these call sites is a screen a learner or an admin
 * reads today.
 */

/**
 * Parse a deadline safely. Deadlines are DATE values that arrive as UTC
 * midnight; taking the calendar day directly keeps the displayed day from
 * shifting back a day in negative-offset timezones.
 */
function parseCalendarDay(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00`);
}

function withYearIfNotCurrent(d: Date, now: Date): Intl.DateTimeFormatOptions {
  return {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}),
  };
}

/** Format a DATE-valued deadline for a card or list row, e.g. "Feb 5, 2027". */
export function formatDueDay(iso: string, now: Date = new Date()): string {
  const d = parseCalendarDay(iso);
  return d.toLocaleDateString('en-US', withYearIfNotCurrent(d, now));
}

/** Format a real timestamp (e.g. completedAt) as a local calendar date. */
export function formatEventDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', withYearIfNotCurrent(d, now));
}

/**
 * Format a DATE-valued deadline with the year always shown, e.g. "Sep 15,
 * 2026". Four screens use this wording: the admin course detail, the admin
 * courses list' group helpers, the learner course detail and the learner
 * module table.
 */
export function formatDueDateLong(iso: string): string {
  return parseCalendarDay(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a DATE-valued deadline in the browser's locale-default numeric form,
 * e.g. "9/15/2026" in en-US. Only the admin courses table uses this.
 */
export function formatDueDateNumeric(iso: string): string {
  return parseCalendarDay(iso).toLocaleDateString();
}

/**
 * Relative age, compact — "5m ago", "2h ago", "3d ago". This is the
 * notification bell's voice, where the column is a few characters wide.
 *
 * There are deliberately TWO relative formatters (see `timeAgoLong`). The two
 * surfaces are never on screen together — the bell reads `issued_at` from
 * /badges/me and /certificates/me, the admin dashboard's activity feed reads
 * `timestamp` from /courses/analytics/activity — and the wording difference
 * looks intentional (a 320px popover versus a roomy card). Collapsing them to
 * one voice is a product decision, not a refactor, so both live here with
 * their existing output preserved exactly.
 */
export function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const mins = minutesSince(dateStr);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return shortDay(dateStr);
}

/**
 * Relative age, verbose — "5 min ago", "2 hours ago", "3 days ago". The admin
 * dashboard activity feed's voice. See `timeAgo` for why there are two.
 */
export function timeAgoLong(dateStr: string): string {
  if (!dateStr) return '';
  const mins = minutesSince(dateStr);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return shortDay(dateStr);
}

function minutesSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000);
}

/** Both relative formatters fall back to the same absolute day past a week. */
function shortDay(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Due-date pill colours, shared by the learner dashboard and catalog.
 * Overdue is the critical (red) token — a compliance LMS must be able to say
 * "you are late" (live walkthrough finding).
 */
export const DEADLINE_CLASSES: Partial<Record<LearnerDeadlineStatus, string>> = {
  overdue: 'bg-status-critical-bg text-status-critical-fg',
  at_risk: 'bg-status-warning-bg text-status-warning-fg',
  on_track: 'bg-status-success-bg text-status-success-fg',
  completed: 'bg-status-success-bg text-status-success-fg',
};
